/**
 * Multi-form XLSX workbook → Form Builder converter.
 *
 * Each SHEET (tab) of the workbook becomes its own form. Reuses the single-form
 * converter per sheet so behaviour stays identical.
 */

import * as XLSX from 'xlsx';
import type {
  ExcelRow,
  ImportFormMetadata,
  SheetConversionResult,
  WorkbookConversionResult,
} from './excelImportTypes';
import { convertRowsToForm, trimRows, validateSheetStructure } from './excelToFormConverter';

/** Scaffolding tab names (matched case-insensitively) that are not forms. */
const IGNORED_SHEET_NAMES = new Set(['instructions', 'readme', 'read me', 'guide', 'help']);

const isIgnoredSheet = (sheetName: string): boolean =>
  IGNORED_SHEET_NAMES.has(sheetName.trim().toLowerCase());

const parseSheet = (worksheet: XLSX.WorkSheet): ExcelRow[] => {
  const jsonData = XLSX.utils.sheet_to_json(worksheet, {
    raw: false,
    defval: '',
    blankrows: false,
  }) as ExcelRow[];
  return trimRows(jsonData);
};

/**
 * Parse a workbook file and convert every form sheet into a form definition.
 */
export const convertWorkbookToForms = (
  file: File,
  metadata: ImportFormMetadata,
  currentUserId: string | number,
): Promise<WorkbookConversionResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const errors: string[] = [];
      try {
        const workbook = XLSX.read(e.target?.result, { type: 'binary' });

        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          resolve({ sheets: [], skippedSheets: [], errors: ['Workbook has no sheets'] });
          return;
        }

        const sheets: SheetConversionResult[] = [];
        const skippedSheets: string[] = [];

        workbook.SheetNames.forEach((sheetName) => {
          if (isIgnoredSheet(sheetName)) {
            skippedSheets.push(sheetName);
            return;
          }

          const rows = parseSheet(workbook.Sheets[sheetName]);
          if (!rows || rows.length === 0) {
            skippedSheets.push(sheetName);
            return;
          }

          const structureErrors = validateSheetStructure(rows);
          if (structureErrors.length > 0) {
            sheets.push({
              sheetName,
              success: false,
              data: null,
              errors: structureErrors.map((msg) => `Sheet "${sheetName}": ${msg}`),
              warnings: [],
              sectionCount: 0,
              controlCount: 0,
            });
            return;
          }

          const result = convertRowsToForm(
            rows,
            { ...metadata, name: sheetName.trim() },
            currentUserId,
          );
          const sectionCount = result.data?.sections.length ?? 0;
          const controlCount =
            result.data?.sections.reduce((sum, s) => sum + s.controls.length, 0) ?? 0;

          sheets.push({
            sheetName,
            success: result.success,
            data: result.data,
            errors: result.errors.map((msg) => `Sheet "${sheetName}": ${msg}`),
            warnings: result.warnings.map((msg) => `Sheet "${sheetName}": ${msg}`),
            sectionCount,
            controlCount,
          });
        });

        if (sheets.length === 0) {
          errors.push('No form sheets found. Every tab was empty or skipped.');
        }

        resolve({ sheets, skippedSheets, errors });
      } catch (error: any) {
        resolve({
          sheets: [],
          skippedSheets: [],
          errors: [`Workbook parsing failed: ${error.message}`],
        });
      }
    };

    reader.onerror = () => {
      resolve({ sheets: [], skippedSheets: [], errors: ['Failed to read workbook file'] });
    };

    reader.readAsBinaryString(file);
  });
};
